import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Clock, Languages } from "lucide-react";
import VerifiedBadge from "../components/VerifiedBadge";
import { useSeo } from "../lib/seo";

export default function TeacherDetail() {
  const { id } = useParams();
  const [guru, setGuru] = useState(null);
  const [service, setService] = useState(null);
  const [form, setForm] = useState({ booker_name: "", booker_email: "", booker_phone: "", preferred_slot: "", notes: "" });
  const [paying, setPaying] = useState(false);

  useEffect(() => { api.get(`/gurus/${id}`).then((r) => setGuru(r.data)); }, [id]);
  useSeo(guru?.name, guru && {
    "@context": "https://schema.org", "@type": "Person", name: guru.name, jobTitle: guru.title, description: guru.bio?.slice(0, 300),
    image: guru.photo_url || undefined, address: guru.location || undefined, knowsLanguage: guru.languages,
    url: `${window.location.origin}/teachers/${guru.slug || guru.id}`,
    makesOffer: (guru.services || []).map((s) => ({ "@type": "Offer", name: s.title, price: s.price_usd, priceCurrency: "USD" })),
  });

  const book = async () => {
    if (!service) return;
    if (!form.booker_name || !form.booker_email) return toast.error("Name and email required");
    setPaying(true);
    try {
      const { data } = await api.post("/gurus/book", {
        service_id: service.id, ...form, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e)); setPaying(false); }
  };

  if (!guru) return <div className="p-10 text-[#63534E]">Loading…</div>;

  return (
    <div data-testid="teacher-detail" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="grid lg:grid-cols-3 gap-8">
        <aside className="bg-[#FFFDF9] sacred-border rounded-3xl overflow-hidden h-fit lg:sticky lg:top-24">
          <div className="h-56 bg-gradient-to-br from-[#4A0E17] to-[#36080F] flex items-center justify-center">
            {guru.photo_url ? <img src={guru.photo_url} alt={guru.name} className="w-full h-full object-cover" /> : <div className="font-devanagari text-[100px] text-[#D4AF37] opacity-40">ॐ</div>}
          </div>
          <div className="p-6">
            <h1 className="font-serif text-2xl text-[#4A0E17] flex items-center gap-1.5">{guru.name} <VerifiedBadge show={guru.verified} label /></h1>
            <div className="text-sm text-[#63534E] mt-1">{guru.title}</div>
            {guru.location && <div className="flex items-center gap-1.5 text-sm text-[#63534E] mt-3"><MapPin className="w-4 h-4" /> {guru.location}</div>}
            <div className="flex items-center gap-1.5 text-sm text-[#63534E] mt-2"><Languages className="w-4 h-4" /> {guru.languages?.join(", ")}</div>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {(guru.categories || []).map((c) => (
                <span key={c} className="text-xs bg-[#F3E5AB]/60 border border-[#D4AF37]/30 rounded-full px-2 py-0.5 capitalize">{c}</span>
              ))}
            </div>
          </div>
        </aside>

        <div className="lg:col-span-2">
          <div className="overline">About</div>
          <p className="text-[#1F1412] mt-3 leading-relaxed whitespace-pre-line">{guru.bio}</p>

          <div className="mt-10 overline">Services offered</div>
          {(!guru.services || guru.services.length === 0) ? (
            <div className="mt-5 bg-[#FFFDF9] sacred-border rounded-2xl p-8 text-center text-[#63534E]">
              This teacher is setting up services. Please check back soon.
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              {guru.services.map((s) => (
                <div key={s.id} data-testid={`service-${s.id}`} className="bg-[#FFFDF9] sacred-border rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-[#F3E5AB]/60 border border-[#D4AF37]/30 rounded-full px-2 py-0.5 capitalize">{s.category}</span>
                      <span className="flex items-center gap-1 text-xs text-[#63534E]"><Clock className="w-3.5 h-3.5" /> {s.duration_min} min</span>
                    </div>
                    <h3 className="font-serif text-xl text-[#4A0E17] mt-2">{s.title}</h3>
                    <p className="text-sm text-[#63534E] mt-2">{s.description}</p>
                  </div>
                  <div className="text-right sm:w-40">
                    <div className="font-mono text-2xl font-semibold text-[#E65100]">${s.price_usd.toFixed(0)}</div>
                    <Dialog onOpenChange={(o) => { if (o) setService(s); }}>
                      <DialogTrigger asChild>
                        <Button data-testid={`book-${s.id}`} className="mt-3 bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-10 text-sm w-full">Book this</Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#FFFDF9] border-[#D4AF37]/40 max-w-md">
                        <DialogHeader>
                          <DialogTitle className="font-serif text-xl text-[#4A0E17]">Book {service?.title}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 mt-2">
                          <Input data-testid="book-name" placeholder="Your name" value={form.booker_name} onChange={(e) => setForm({ ...form, booker_name: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                          <Input data-testid="book-email" type="email" placeholder="Email" value={form.booker_email} onChange={(e) => setForm({ ...form, booker_email: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                          <Input data-testid="book-phone" placeholder="Phone (optional)" value={form.booker_phone} onChange={(e) => setForm({ ...form, booker_phone: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                          <Input data-testid="book-slot" placeholder="Preferred date/time (free text)" value={form.preferred_slot} onChange={(e) => setForm({ ...form, preferred_slot: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                          <Textarea data-testid="book-notes" rows={3} placeholder="Any specific intention or question" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white border-[#D4AF37]/40" />
                          <div className="flex items-center justify-between pt-3 border-t border-[#D4AF37]/30 font-semibold text-[#4A0E17]">
                            <span>Total</span>
                            <span className="font-mono text-[#E65100] text-xl">${service?.price_usd?.toFixed(2) || "—"}</span>
                          </div>
                          <Button data-testid="book-confirm" disabled={paying} onClick={book} className="w-full bg-[#E65100] hover:bg-[#BF360C] rounded-full h-12">
                            {paying ? "Redirecting…" : "Pay & book securely"}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
